import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Lock, FileCheck } from 'lucide-react';
import { BRANCH_KEYS, SUB_KEYS, usePolicy, setLocalHidden, setHandoffOn, kritisStatus, kritisSetup, kritisChange, kritisProof } from '../lib/policy';
import { kritisPdf, hiddenNames, logAction } from '../lib/kritisPdf';
import { KritisMaps, OwnMaps } from './OwnMaps';
import { enterpriseLicense } from '../lib/license';

// branches + Leakbuster areas as checkboxes (checked = visible)
export function BranchPicker({ t, value, disabled, onChange, testid = 'branch' }) {
  const flip = (list, k) => (list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const setB = (k) => { const b = flip(value.branches, k); if (b.length >= BRANCH_KEYS.length) return toast.error(t.pvNeedOne); onChange({ ...value, branches: b }); };
  return (
    <div className="pv-list" data-testid={`${testid}-picker`}>
      {BRANCH_KEYS.map((k) => (
        <div key={k} className="pv-item">
          <label><input type="checkbox" data-testid={`${testid}-${k}`} disabled={disabled} checked={!value.branches.includes(k)} onChange={() => setB(k)} /> {t['pf_' + k]}</label>
          {k === 'detect' && !value.branches.includes('detect') && (
            <div className="pv-subs">
              {SUB_KEYS.map((s) => <label key={s}><input type="checkbox" data-testid={`${testid}-sub-${s}`} disabled={disabled} checked={!value.subs.includes(s)} onChange={() => onChange({ ...value, subs: flip(value.subs, s) })} /> {t['dsub_' + s]}</label>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const errText = (t, e) => t['krErr_' + e.message] || t.licOffline;

function KritisForm({ t, st, onDone }) {
  const [draft, setDraft] = useState(st.policy);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const setup = !st.has_admin;
  const save = async () => {
    if (setup && (pw.length < 8 || pw !== pw2)) return toast.error(t.krPwRule);
    if (!setup && !pw) return toast.error(t.krPwNeed);
    if (!setup && pw2 && pw2.length < 8) return toast.error(t.krPwRule);
    setBusy(true);
    try { onDone(setup ? await kritisSetup(pw, draft) : await kritisChange(pw, draft, pw2 || null)); setPw(''); setPw2(''); toast.success(t.krSaved); } catch (e) { toast.error(errText(t, e)); }
    setBusy(false);
  };
  const proof = async () => {
    if (!pw) return toast.error(t.knPwNeed);
    setBusy(true);
    try { kritisPdf(t, await kritisProof(pw)); setPw(''); } catch (e) { toast.error(errText(t, e)); }
    setBusy(false);
  };
  return (
    <div className="kr-form" data-testid="kritis-form">
      <label className="kr-switch"><input type="checkbox" data-testid="kritis-toggle" checked={draft.kritis} onChange={(e) => setDraft({ ...draft, kritis: e.target.checked })} /> <b>{t.krSwitch}</b></label>
      <small className="muted">{t.krSwitchHint}</small>
      <b className="small-hint">{t.krBranches}</b>
      <BranchPicker t={t} value={draft} onChange={setDraft} testid="kritis-branch" />
      <KritisMaps t={t} draft={draft} setDraft={setDraft} />
      <input type="password" className="input" autoComplete="new-password" data-testid="kritis-password-input" placeholder={setup ? t.krPwNew : t.krPwAdmin} value={pw} onChange={(e) => setPw(e.target.value)} />
      <input type="password" className="input" autoComplete="new-password" data-testid="kritis-password2-input" placeholder={setup ? t.krPwRepeat : t.krPwChange} value={pw2} onChange={(e) => setPw2(e.target.value)} />
      <button className="btn btn-dark btn-sm" disabled={busy} data-testid="kritis-save-btn" onClick={save}><Lock size={14} /> {setup ? t.krSetup : t.krApply}</button>
      {!setup && <button className="btn btn-outline btn-sm" disabled={busy} data-testid="kritis-proof-btn" onClick={proof}><FileCheck size={14} /> {t.knBtn}</button>}
      <small className="muted">{t.krSync}</small>
      <KritisLog t={t} log={st.log || []} />
    </div>
  );
}

function KritisLog({ t, log }) {
  if (!log.length) return null;
  return (
    <div className="kr-log" data-testid="kritis-log">
      <b className="small-hint">{t.klTitle}</b>
      <ul>
        {log.map((e, i) => (
          <li key={e.at + i} data-testid={`kritis-log-${i}`}>
            <span>{new Date(e.at).toLocaleString(t.locale)}</span> · <b>{logAction(t, e.action)}</b> · {e.device === 'owner' ? t.klOwner : e.device}
            {e.action !== 'reset' && <> · {e.kritis ? t.knOn : t.knOff}{hiddenNames(t, e) ? ` · ${t.knHidden}: ${hiddenNames(t, e)}` : ''}</>}
          </li>
        ))}
      </ul>
    </div>
  );
}


// company admin: KRITIS switch + branches for all devices of the enterprise licence
function KritisAdmin({ t }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { kritisStatus().then(setSt, (e) => setErr(errText(t, e))); }, [t]);
  return (
    <details className="md-block" data-testid="kritis-admin">
      <summary data-testid="kritis-admin-toggle"><ShieldCheck size={14} /> {t.krTitle} – {enterpriseLicense()?.ent}</summary>
      <p className="muted small-hint">{t.krIntro}</p>
      {err && <p className="muted" data-testid="kritis-error">{err}</p>}
      {st && <KritisForm t={t} st={st} onDone={setSt} />}
    </details>
  );
}

export default function PolicySettings({ t }) {
  const { kritis, hidden, handoff } = usePolicy();
  return (
    <section className="set-sec" data-testid="policy-settings">
      <h5>🧩 {t.pvTitle}</h5>
      {kritis && <p className="kr-note" data-testid="kritis-active-note">🛡️ {t.krActive}</p>}
      <small className="muted">{t.pvHint}</small>
      <BranchPicker t={t} value={hidden} disabled={kritis} onChange={setLocalHidden} />
      <label className="pv-ho"><input type="checkbox" data-testid="handoff-enable-toggle" disabled={kritis} checked={handoff} onChange={(e) => setHandoffOn(e.target.checked)} /> 🔒 {t.hoEnable}</label>
      <small className="muted">{t.hoEnableHint}</small>
      {enterpriseLicense() && <KritisAdmin t={t} />}
      <OwnMaps t={t} />
    </section>
  );
}
